import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Mountain, Coffee, Palmtree, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TripFinderMatrix = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredCell, setHoveredCell] = useState(null);

  const regions = [
    { 
      key: 'north_central_america', 
      name: 'North & Central America', 
      name_es: 'Norte y Centroamérica',
      icon: Mountain,
      color: 'from-emerald-500 to-teal-600'
    },
    { 
      key: 'south_america', 
      name: 'South America', 
      name_es: 'Sudamérica',
      icon: Mountain,
      color: 'from-orange-500 to-amber-600'
    },
    { 
      key: 'caribbean', 
      name: 'Caribbean', 
      name_es: 'Caribe',
      icon: Palmtree,
      color: 'from-cyan-500 to-blue-600'
    }
  ];

  const vibes = [
    { 
      key: 'adventure', 
      name: 'Adventure', 
      name_es: 'Aventura',
      icon: Compass,
      emoji: '🏔️'
    },
    { 
      key: 'culture', 
      name: 'Culture', 
      name_es: 'Cultura',
      icon: Coffee,
      emoji: '🎭'
    },
    { 
      key: 'relaxation', 
      name: 'Relaxation', 
      name_es: 'Relajación',
      icon: Sparkles,
      emoji: '🌴'
    }
  ];

  useEffect(() => {
    const fetchMatrix = async () => {
      try {
        const response = await fetch(`${API_URL}/api/trips/matrix?lang=${language}`);
        const data = await response.json();
        setMatrix(data.matrix);
      } catch (error) {
        console.error('Error fetching trip matrix:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMatrix();
  }, [language]);

  const handleCellClick = (regionKey, vibeKey) => {
    navigate(`/trips?region=${regionKey}&vibe=${vibeKey}`);
  };

  const getCellCount = (regionKey, vibeKey) => {
    if (!matrix || !matrix[regionKey]) return 0;
    return matrix[regionKey][vibeKey] || 0;
  };

  if (loading) {
    return (
      <div className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded w-1/3 mx-auto"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-gray-900 to-gray-800" data-testid="trip-finder-matrix">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#84bc00]/20 text-[#84bc00] text-sm font-bold rounded-full mb-4">
            {language === 'es' ? 'ENCUENTRA TU VIAJE' : 'FIND YOUR TRIP'}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
            {language === 'es' ? '¿Qué buscas?' : 'What are you looking for?'}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Elige tu región y vibra para encontrar el viaje perfecto' 
              : 'Pick your region and vibe to find the perfect trip'}
          </p>
        </div>

        {/* Trip Finder Matrix */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            {/* Header Row - Vibes */}
            <thead>
              <tr>
                <th className="p-4"></th>
                {vibes.map((vibe) => (
                  <th key={vibe.key} className="p-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-3xl">{vibe.emoji}</span>
                      <span className="text-white font-bold text-sm sm:text-base">
                        {language === 'es' ? vibe.name_es : vibe.name}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            
            {/* Body - Regions × Vibes */}
            <tbody>
              {regions.map((region) => (
                <tr key={region.key}>
                  {/* Region Label */}
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${region.color} flex items-center justify-center`}>
                        <region.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-white font-bold text-sm sm:text-base whitespace-nowrap">
                        {language === 'es' ? region.name_es : region.name}
                      </span>
                    </div>
                  </td>
                  
                  {/* Matrix Cells */}
                  {vibes.map((vibe) => {
                    const count = getCellCount(region.key, vibe.key);
                    const isHovered = hoveredCell === `${region.key}-${vibe.key}`;
                    const hasTrips = count > 0;
                    
                    return (
                      <td key={`${region.key}-${vibe.key}`} className="p-2">
                        <button
                          data-testid={`matrix-cell-${region.key}-${vibe.key}`}
                          onClick={() => hasTrips && handleCellClick(region.key, vibe.key)}
                          onMouseEnter={() => setHoveredCell(`${region.key}-${vibe.key}`)}
                          onMouseLeave={() => setHoveredCell(null)}
                          disabled={!hasTrips}
                          className={`
                            w-full h-24 sm:h-28 rounded-xl transition-all duration-300
                            flex flex-col items-center justify-center gap-2
                            ${hasTrips 
                              ? `bg-gray-800 hover:bg-gradient-to-br hover:${region.color} cursor-pointer border-2 border-gray-700 hover:border-[#84bc00]` 
                              : 'bg-gray-800/50 cursor-not-allowed border border-gray-700/50 opacity-50'
                            }
                            ${isHovered && hasTrips ? 'scale-105 shadow-xl shadow-[#84bc00]/20' : ''}
                          `}
                        >
                          <span className={`text-3xl font-black ${hasTrips ? 'text-[#84bc00]' : 'text-gray-600'}`}>
                            {count}
                          </span>
                          <span className={`text-xs sm:text-sm ${hasTrips ? 'text-gray-300' : 'text-gray-600'}`}>
                            {count === 1 
                              ? (language === 'es' ? 'viaje' : 'trip') 
                              : (language === 'es' ? 'viajes' : 'trips')}
                          </span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* View All CTA */}
        <div className="text-center mt-10">
          <button
            data-testid="view-all-trips-btn"
            onClick={() => navigate('/trips')}
            className="px-8 py-4 bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold rounded-lg transition-all duration-300 hover:scale-105"
          >
            {language === 'es' ? 'VER TODOS LOS VIAJES' : 'VIEW ALL TRIPS'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default TripFinderMatrix;
